/* tslint:disable:ban-types */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '~app/shared/models/user.model';
import { CHECKIN_STEPS } from '~app/shared/constants/checkin-steps.constant';
import { StepperAssignmentURLSteps } from '~app/shared/enums/stepper-assignment-urls-steps.constant';
import { IStepperProcessSteps, IStep } from '~app/shared/interfaces/process-step.interface';
import { Subject } from 'rxjs';
import { StepperAssignmentType } from '~app/shared/types/stepper-assignment-type.type';
import { CHECKOUT_STEPS } from '~app/shared/constants/checkout-steps.constant';
import { STEPPER_PROCESS } from '~app/shared/constants/local-storage-keys.constant';
import { ILocalStorageStepper } from '~app/shared/interfaces/local-storage-stepper.interface';

@Injectable({
  providedIn: 'root',
})
export class CheckinStepperService {
  public stepperAssignmentType: StepperAssignmentType;
  public assignmentSteps: IStepperProcessSteps;
  public stepperAssignmentType$ = new Subject<StepperAssignmentType>();
  constructor(public readonly router: Router) {
    this.stepperAssignmentType$.subscribe(type => this.setProcessType(type));
    const { currentProcessType } = this.stepperFromLocalStorage || {};
    if (currentProcessType) this.onProcessTypeChanged(currentProcessType);
  }

  public get stepperFromLocalStorage(): ILocalStorageStepper {
    return JSON.parse(localStorage.getItem(STEPPER_PROCESS)) as ILocalStorageStepper;
  }

  public clearStepperFromLocalStorage(): void {
    localStorage.removeItem(STEPPER_PROCESS);
  }

  public onProcessTypeChanged(value: StepperAssignmentType = 'check-in'): void {
    this.stepperAssignmentType$.next(value);
  }

  public setProcessType(value: StepperAssignmentType): void {
    if (!value) return;
    this.assignmentSteps = value === 'check-out' ? CHECKOUT_STEPS : CHECKIN_STEPS;
    this.stepperAssignmentType = value;
    localStorage.setItem(STEPPER_PROCESS, JSON.stringify({ currentProcessType: value } as ILocalStorageStepper));
  }

  public getStepProgress(isContractor: boolean, isCurrent = false): number {
    const urlSegment = this.getURLSegment(this.router.url);
    const step = isCurrent
      ? this.getStepWithUrl(urlSegment as StepperAssignmentURLSteps)
      : this.getNextStepWithPreviousURL(urlSegment as StepperAssignmentURLSteps, isContractor);

    if (!step) return;

    return isContractor ? step.progress.isContractor : step.progress.isCrewMember;
  }

  public resumeCheckin(user: User, processType: StepperAssignmentType): void {
    this.onProcessTypeChanged(processType);
    const progress =
      processType === 'check-in'
        ? user.nextFutureAssignment.workflowState.preCheckStatus
        : user.nextFutureSignOffAssignment.workflowState.debarkStatus;
    const isContractor = user.profile?.isContractor;

    const step = this.getStepWithProgress(progress, isContractor);

    if (!step) this.exitStepper();
    if (step && progress === 0) {
      this.goToURL(`${this.stepperAssignmentType}/${Object.keys(this.assignmentSteps)[0]}`);
      return;
    }
    if (step) {
      this.goToURL(`${this.stepperAssignmentType}/${this.getStepRedirection(step, isContractor, 'next')}`);
    }
  }

  public exitStepper(): void {
    this.goToURL('welcome', '');
  }

  public previousStep(isContractor: boolean): void {
    const urlSegment = this.getURLSegment(this.router.url) as StepperAssignmentURLSteps;
    const step = this.getStepWithUrl(urlSegment);
    if (step && step.redirections.previous !== StepperAssignmentURLSteps.IDLE) {
      this.goToURL(`${this.stepperAssignmentType}/${this.getStepRedirection(step, isContractor, 'previous')}`);
      return;
    }
    this.goToURL(StepperAssignmentURLSteps.IDLE, '');
  }

  public nextStep(isContractor: boolean): void {
    const urlSegment = this.getURLSegment(this.router.url);
    const step = this.getStepWithUrl(urlSegment as StepperAssignmentURLSteps);
    let nextFragment;

    if (step?.redirections?.next instanceof Function) {
      nextFragment = step.redirections.next(isContractor);
    } else {
      nextFragment = step?.redirections?.next;
    }

    if (step && nextFragment !== StepperAssignmentURLSteps.IDLE) {
      this.goToURL(`${this.stepperAssignmentType}/${nextFragment}`);
      return;
    }
    this.goToURL(StepperAssignmentURLSteps.IDLE, '');
  }

  public getStepWithUrl(urlSegment: StepperAssignmentURLSteps): IStep {
    let step: IStep;
    if (urlSegment in this.assignmentSteps) {
      step = this.assignmentSteps[urlSegment];
    }
    return step;
  }

  public getStepWithProgress(progress: number, isContractor = false): IStep {
    const steps = Object.values(this.assignmentSteps).filter((value, index) => {
      const stepProgress = isContractor ? value.progress.isContractor : value.progress.isCrewMember;
      const firstStepCondition = progress === 0 && index === 0;
      return stepProgress <= progress || firstStepCondition;
    });
    return steps.length && steps[steps.length - 1];
  }
  public goToURL(url: string, baseURL = 'stepper/'): void {
    this.router.navigate([baseURL + url]);
  }

  public getURLSegment(url: string): string {
    return url.split('/').pop();
  }

  public getNextStepWithPreviousURL(url: StepperAssignmentURLSteps, isContractor: boolean): IStep {
    const nextStepKey = this.getStepRedirection(this.getStepWithUrl(url), isContractor, 'previous');
    return this.getStepWithUrl(nextStepKey as StepperAssignmentURLSteps);
  }

  public getStepRedirection(step: IStep, isContractor: boolean, direction: 'next' | 'previous'): string {
    return step?.redirections[direction] instanceof Function
      ? (step?.redirections[direction] as Function)(isContractor)
      : step?.redirections[direction];
  }
}
